import React from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  // og:* and twitter:* default to `title` / `description`. Override when the
  // social-share copy should differ from the SERP title (typically shorter
  // and without rank-bait suffixes like "| Рост продаж на 40-60%").
  ogTitle?: string;
  ogDescription?: string;
  children?: React.ReactNode;
}

// React 19 hoists <title>, <meta>, <link>, and <script> elements rendered
// anywhere in the tree into <head> automatically. No HelmetProvider needed.
// https://react.dev/reference/react-dom/components/title
const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  url,
  type,
  ogTitle,
  ogDescription,
  children,
}) => {
  const socialTitle = ogTitle ?? title;
  const socialDescription = ogDescription ?? description;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: 'ЛОНА ФИНАНС',
    description,
    url,
    logo: 'https://lona.finance/small-logo.svg',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: 'Russian',
      areaServed: 'RU',
      url: 'https://t.me/lona_manager',
    },
    sameAs: ['https://t.me/lona_manager'],
    offers: {
      '@type': 'Offer',
      name: 'B2B Кредитование',
      description: 'Кредитование покупателей для увеличения B2B продаж',
      price: '0',
      priceCurrency: 'RUB',
      eligibleRegion: {
        '@type': 'Country',
        name: 'Russia',
      },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '100',
      bestRating: '5',
      worstRating: '1',
    },
  };

  return (
    <>
      {title ? <title>{title}</title> : null}
      {title ? <meta name="title" content={title} /> : null}
      {description ? <meta name="description" content={description} /> : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="author" content="ЛОНА ФИНАНС" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="Russian" />

      {url ? <link rel="canonical" href={url} /> : null}

      {/* Open Graph / Facebook */}
      {type ? <meta property="og:type" content={type} /> : null}
      {url ? <meta property="og:url" content={url} /> : null}
      {socialTitle ? <meta property="og:title" content={socialTitle} /> : null}
      {socialDescription ? <meta property="og:description" content={socialDescription} /> : null}
      {image ? <meta property="og:image" content={image} /> : null}
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="ЛОНА ФИНАНС" />
      <meta property="og:locale" content="ru_RU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {url ? <meta name="twitter:url" content={url} /> : null}
      {socialTitle ? <meta name="twitter:title" content={socialTitle} /> : null}
      {socialDescription ? <meta name="twitter:description" content={socialDescription} /> : null}
      {image ? <meta name="twitter:image" content={image} /> : null}

      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      {children}
    </>
  );
};

export default SEO;
